import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Helmet from 'react-helmet';
import { useHistory, useParams } from 'react-router';
import { useIntl } from 'react-intl';
import { Pagination } from '@tovia/man-ui';
import PaginationModal from 'src/client/components/modals/PaginationModal';
import LoaderWrapper from 'src/client/components/Loader/LoaderWrapper';
import { loadBanner } from 'src/client/redux/modules/updates';
import { setTitle } from 'src/client/redux/modules/app';
import { UpdateStreamCard } from 'src/client/components/cards';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import { generateGalleryAlt } from '../../../utils/generateGalleryAlt';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';

const pageTitle = 'Beauty of the Day';

export default function BotdArchive() {
  const [statePage, setStatePage] = useState<number>(1);

  const site = useSelector((state) => state.site);
  const pageSize = useSelector((state) => state.galleries.pageSize);
  const { bannersError: error, loadingBanners: loading, bannersTotal, banners } = useSelector((state) => state.updates);
  const { config, contentCdnUrl } = useSelector((state) => state.app);

  const dispatch = useDispatch();
  const { page } = useParams<{ page: string }>();
  const history = useHistory();
  const intl = useIntl();
  const showSfwSite = useShowSfwSite();

  const loadData = useCallback(() => {
    const {
      galleries: {
        botd: { name },
      },
    } = config;

    const loadParams = {
      name,
      first: pageSize,
      page: page || 1,
      timestamp: true,
    };

    dispatch(loadBanner(loadParams));
  }, [config, dispatch, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const title = `${pageTitle} ${(page && `Page: ${page}`) || ''}`;
    dispatch(setTitle(title));
  }, [dispatch, page]);

  const pageUrlPrefix = useMemo(() => '/photo-archive', []);

  const setPage = useCallback(
    (newPage) => {
      history.push(`${pageUrlPrefix}/${newPage}`);
    },
    [history, pageUrlPrefix],
  );

  const title = `${intl.formatMessage({
    id: 'galleries.botd.title',
    defaultMessage: `${pageTitle} ${(page && `Page: ${page}`) || ''}`,
  })} ${(page && `Page: ${page}`) || ''}`;

  useEffect(() => {
    if (page) {
      setStatePage(+(page || 1));
    }
  }, [setStatePage, page]);

  const renderGalleriesContent = () =>
    banners.map((gallery, i) => {
      const { type, models, galleryUUID, path, photographers, imageCount } = gallery;

      if (!gallery.siteUUID) {
        gallery.siteUUID = site.UUID;
      }

      // Slideshow images do not use the same covers as anything else, so we
      // don't bother using the `getGalleryCoverUrl` hook and function here.
      // There are currently no plans to make these slides available as non-nude.
      const imagePath = `${gallery.slideUUID}.jpg`;
      const galleryCoverUrl = [contentCdnUrl, gallery.siteUUID, 'media', 'slideshow', imagePath].join('/');

      const galleryProps = {
        UUID: galleryUUID,
        name: models.map((model) => model).join(', '),
        publishedAt: '',
        type,
        ratings: 0,
        ratingAverage: 0,
        previewMode: false,
        galleryUrl: path,
        galleryCoverUrl,
        modelName: models.map((model) => model).join(', '),
        runtime: '',
        imageCount,
        photographerName: photographers.map((photog) => photog).join(', '),
        showCounts: false,
        showGalleryName: false,
        showInformation: false,
        showTextOnHover: true,
      };

      // It's bad practice to use the array index as part of the key, but domai
      // does actually re-use the galleries here so we have to do something.
      return (
        <UpdateStreamCard
          title=""
          alt={generateGalleryAlt(gallery.name, models)}
          key={galleryUUID + i}
          isBlurred={showSfwSite}
          {...galleryProps}
        />
      );
    });

  const canonicalUrl = 'photo-archive';

  return (
    <div>
      <Helmet>
        <title>{`${site.name} - ${title.trim()}`}</title>
        <meta property="og:title" content={`${site.name} - ${title.trim()}`} />
        <meta property="og:url" content={`https://www.${site.domain}/${canonicalUrl}`} />
        <meta property="prerender:ttl" content="86400" />
      </Helmet>
      {loading || error ? (
        <LoaderWrapper
          defaultHeight
          reloadFunction={loadData}
          loadingStatus={loading}
          reduxErrorStatus={error}
          contentType="galleries"
        />
      ) : (
        <div>
          {renderGalleriesContent()}
          <Pagination
            activePage={statePage}
            boundaryRange={1}
            onPageChange={setPage}
            pageUrlPrefix={pageUrlPrefix}
            siblingRange={1}
            totalPages={Math.ceil(bannersTotal / pageSize)}
            showPrevNext
            modal={
              <PaginationModal
                setPage={(pageNumber) => setPage(pageNumber)}
                totalPages={Math.ceil(bannersTotal / pageSize)}
              />
            }
          />
        </div>
      )}
    </div>
  );
}
